var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Authentication Context
 *
 * Provides authentication state and methods to the React application.
 * Manages user login, logout, and session persistence.
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { gql } from '@apollo/client';
// GraphQL Mutations and Queries
const LOGIN_MUTATION = gql `
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      success
      token
      user {
        _id
        name
        email
        role
        isActive
        createdAt
      }
      error
    }
  }
`;
const ME_QUERY = gql `
  query Me {
    me {
      _id
      name
      email
      role
      isActive
      lastLogin
      createdAt
    }
  }
`;
const LOGOUT_MUTATION = gql `
  mutation Logout {
    logout
  }
`;
/**
 * Create authentication context
 */
const AuthContext = createContext(undefined);
/**
 * Authentication Provider Component
 */
export function AuthProvider({ children, apolloClient }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    // Load user from localStorage on mount
    useEffect(() => {
        const loadUser = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const storedToken = localStorage.getItem('auth_token');
                if (storedToken) {
                    // Verify token by fetching current user
                    const { data } = yield apolloClient.query({
                        query: ME_QUERY,
                        fetchPolicy: 'network-only', // Skip cache to verify token is still valid
                    });
                    if (data === null || data === void 0 ? void 0 : data.me) {
                        setUser(data.me);
                        setToken(storedToken);
                    }
                    else {
                        // Token is invalid, clear it
                        localStorage.removeItem('auth_token');
                        setToken(null);
                        setUser(null);
                    }
                }
            }
            catch (error) {
                console.error('Failed to load user:', error);
                localStorage.removeItem('auth_token');
                setToken(null);
                setUser(null);
            }
            finally {
                setLoading(false);
            }
        });
        loadUser();
    }, [apolloClient]);
    /**
     * Login function
     */
    const login = (email, password) => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        try {
            const { data } = yield apolloClient.mutate({
                mutation: LOGIN_MUTATION,
                variables: { email, password },
            });
            const response = data === null || data === void 0 ? void 0 : data.login;
            if (!(response === null || response === void 0 ? void 0 : response.success)) {
                throw new Error((response === null || response === void 0 ? void 0 : response.error) || 'Login failed');
            }
            if (!response.token || !response.user) {
                throw new Error('Invalid response from server');
            }
            // Store token and user
            localStorage.setItem('auth_token', response.token);
            setToken(response.token);
            setUser(response.user);
        }
        catch (error) {
            setLoading(false);
            throw error;
        }
        finally {
            setLoading(false);
        }
    });
    /**
     * Logout function
     */
    const logout = () => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        try {
            // Call logout mutation (optional, as JWT is stateless)
            yield apolloClient.mutate({ mutation: LOGOUT_MUTATION });
            // Clear local state
            localStorage.removeItem('auth_token');
            setToken(null);
            setUser(null);
            // Clear Apollo cache
            yield apolloClient.resetStore();
        }
        catch (error) {
            console.error('Logout error:', error);
            // Even if mutation fails, clear local state
            localStorage.removeItem('auth_token');
            setToken(null);
            setUser(null);
            yield apolloClient.resetStore();
        }
        finally {
            setLoading(false);
        }
    });
    /**
     * Check if user has any of the specified roles
     */
    const hasRole = (roles) => {
        if (!user)
            return false;
        return roles.includes(user.role);
    };
    const value = {
        user,
        token,
        login,
        logout,
        isAuthenticated: !!user,
        hasRole,
        loading,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
/**
 * Hook to use authentication context
 */
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
